import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { pluginRoutes } from "@/composition";
import { churchesRoute } from "@/routes/_app/churches";
import { churchDetailRoute } from "@/routes/_app/churches.$churchId";
import { peopleRoute } from "@/routes/_app/people";
import { personDetailRoute } from "@/routes/_app/people.$personId";
import { groupsRoute } from "@/routes/_app/groups";
import { groupDetailRoute } from "@/routes/_app/groups.$groupId";
import { songsRoute } from "@/routes/_app/songs";
import { songDetailRoute } from "@/routes/_app/songs.$songId";
import { meetingsRoute } from "@/routes/_app/meetings";
import { meetingDetailRoute } from "@/routes/_app/meetings.$meetingId";
import { calendarRoute } from "@/routes/_app/calendar";
import { permissionsRoute } from "@/routes/_app/permissions";
import { projectionRoute } from "@/routes/_app/projection";
import { projectorRoute } from "@/routes/_app/projector";
import { financesRoute } from "@/routes/_app/finances";
import { inventoryRoute } from "@/routes/_app/inventory";
import { statsRoute } from "@/routes/_app/stats";
import { networkRoute } from "@/routes/_app/network";
import { publicChurchRoute } from "@/routes/public-church";
import { cc21ProbeRoute } from "@/routes/_app/cc21-probe";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profilePlanRoute } from "@/routes/_app/profile.plan";
import { profileCongregationSettingsRoute } from "@/routes/_app/profile.congregation-settings";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";
import { sandboxModalRoute } from "@/routes/_app/modal";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  // Public page per congregation (F3): real guest-first, no session.
  publicChurchRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    // Regression fixture CC-21 (Select/Popover reachable inside a modal
    // Dialog). DEV only: serves the e2e cc21-probe.spec and is NOT registered
    // in prod.
    ...(import.meta.env.DEV ? [cc21ProbeRoute] : []),
    indexRoute, // path: "/" — Home
    ...pluginRoutes,
    churchesRoute,
    churchDetailRoute,
    peopleRoute,
    personDetailRoute,
    groupsRoute,
    groupDetailRoute,
    songsRoute,
    songDetailRoute,
    meetingsRoute,
    meetingDetailRoute,
    calendarRoute,
    financesRoute,
    inventoryRoute,
    statsRoute,
    networkRoute,
    permissionsRoute,
    projectionRoute,
    projectorRoute,
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profilePlanRoute,
    profileCongregationSettingsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    sandboxModalRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
  // Unknown URL → "page not found" state with a CTA, instead of a blank screen.
  defaultNotFoundComponent: NotFoundRoute,
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
